import React from 'react';
import { observer, inject } from 'mobx-react';
import { observable, action } from 'mobx';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { withNamespaces, Trans } from 'react-i18next';

import { analytics } from 'core/analytics';

import { Divider, SettingsSection } from 'legacyComponents/styled/lib';

import { SectionTitle } from 'apps/group/styles';
import { CreditCard } from 'apps/payment/views/creditCard';
import { Button, Spinner, Paragraph, ConfirmModal } from 'components';
import { PaymentMethodsAppManager } from 'apps/payment/method/appManager';
import { NewCardForm } from 'apps/payment/method/newCardForm';
import { clientStore } from 'apps/client/app';

class UIState {
  @observable showAddPaymentForm = false;
}

@withNamespaces()
@observer
export class PaymentMethodsInfo extends React.Component {
  constructor(props) {
    super(props);
    this.manager = new PaymentMethodsAppManager(null, props);
    this.UIState = new UIState();
  }

  state = {
    open: false,
    deleteCard: null,
  };

  @action addPaymentMethod = () => {
    analytics.sendEvent(
      'InitiateCheckout',
      { value: 5, currency: 'EUR' },
      { twitter: true, facebook: true },
    );
    analytics.linkedinPixel('1169161');
    this.UIState.showAddPaymentForm = true;
  };

  @action cancelAddPayment = () => {
    this.UIState.showAddPaymentForm = false;
  };

  @action handleDeletePaymentMethod = deleteCard => {
    this.setState({ open: true, deleteCard });
  };

  render() {
    const { loading, paymentSpace } = this.manager;
    const { deleteCard, open } = this.state;

    if ((loading || !paymentSpace) && !deleteCard) {
      return <Spinner margin='20px auto' />;
    }

    const { creditCards = [] } = paymentSpace || {};

    let newCardForm = (
      <div
        className='text-blue-500 cursor-pointer'
        data-testid='billingPage-addPaymentMethod-button'
        onClick={() => {
          this.addPaymentMethod();
        }}
      >
        <Trans>+Add New Payment Method</Trans>
      </div>
    );
    if (this.UIState.showAddPaymentForm) {
      newCardForm = (
        <NewCardForm
          paymentMethodsManager={this.manager}
          onCancel={this.cancelAddPayment}
          onSubmitCallback={this.cancelAddPayment}
          responsive
        />
      );
    }

    return (
      <>
        {creditCards.length === 0 ? (
          <>
            <Paragraph>
              <Trans>No payment methods on file</Trans>.{' '}
            </Paragraph>
            {newCardForm}
          </>
        ) : (
          <>
            {creditCards.map(card => {
              const activeButton = card.isDefault ? (
                <p className='text-blue-500' style={{ marginLeft: 12 }}>
                  <Trans>Default</Trans>
                </p>
              ) : (
                <Button
                  text={<Trans>Make default</Trans>}
                  size='small'
                  style={{ fontSize: 16 }}
                  customColor='#0F2EEF'
                  variant='secondary'
                  onClick={card.MakeDefault}
                  outline
                />
              );

              return (
                <CreditCard key={card.id} cardObject={card}>
                  <div
                    onClick={() => {
                      this.handleDeletePaymentMethod(card);
                    }}
                    className='flex mr-3 hover:text-red-500 cursor-pointer'
                  >
                    <Trans>Delete</Trans>
                  </div>
                  {activeButton}
                </CreditCard>
              );
            })}
            {newCardForm}
          </>
        )}

        <ConfirmModal
          open={open}
          loading={deleteCard?.submitting || loading}
          title='Are you sure to delete this card?'
          onConfirm={() => {
            deleteCard.ConfirmDeleteCard({
              successCallback: () => {
                this.setState({
                  open: false,
                  deleteCard: null,
                });
              },
            });
          }}
          onClose={() => this.setState({ open: false })}
        />
      </>
    );
  }
}

@withNamespaces()
@inject('routerStore')
@observer
export class PaymentMethods extends React.Component {
  render() {
    const {
      routerStore: { params },
    } = this.props;
    const spaceSlug = params.get('spacePath');

    return (
      <SettingsSection style={{ marginBottom: '2rem' }}>
        <SectionTitle level={2} size='medium'>
          <Trans>Payment Methods</Trans>
        </SectionTitle>
        <FullWidthDivider />
        <PaymentMethodsInfo spaceSlug={spaceSlug} />
      </SettingsSection>
    );
  }
}

const FullWidthDivider = styled(Divider)`
  width: 100%;
  margin-bottom: 20px;
`;

PaymentMethods.propTypes = {
  routerStore: PropTypes.object,
};
